import type { Step } from '@/app/(platform)/(subscription-gated)/labs/_engine/steps';
import type { LabsStoreApi, SwipeDirection } from '@/app/(platform)/(subscription-gated)/labs/_engine/store';
import type { VerticalPack } from '@/app/(platform)/(subscription-gated)/labs/_engine/types';

/**
 * `?a1=`, the answer a reader gave to the lab's FIRST question while still on the guide page.
 *
 * 🔴 WHY THIS EXISTS. The preview's worst stage is not the lab, it is getting into it. Of a thousand
 * guide-page readers roughly forty click the link, and everything downstream is a fraction of that
 * forty. Asking a reader to leave a page they chose to be on, for an unknown thing, is the whole cost.
 *
 * So the guide page asks the lab's first swipe card itself, as two ordinary links (see
 * `docs/labs-preview-guide-page-snippet.md`). Answering IS the click. The reader arrives having already
 * done something, their answer is banked, and they pick up at card two instead of at a cold welcome
 * screen. Nothing is asked of Webflow beyond pasting static HTML: no iframe, no script, no embed
 * permission, which is what makes it placeable this week.
 *
 * FAITHFULNESS IS THE CONSTRAINT, not click rate. The in-page question is a REAL card from the deck
 * with the deck's own two answers, so the state it seeds is state the reader actually produced. A
 * bespoke hook question tuned for clicks would seed an answer to a question the lab never asks, and the
 * reader would meet a different mechanic one second after committing to this one. Two cards ("which is
 * more you?") was rejected for the same reason: picking A does not mean B is not you, and in a
 * six-card deck seeding that second, invented answer would bias the archetype the reader is here for.
 */

/** The query parameter the guide-page snippet carries. */
export const OPENING_ANSWER_PARAM = 'a1';

/**
 * Where a reader with an opening answer lands: straight into the deck, at card two.
 *
 * Deliberately NOT `welcome` or `m1-intro`. Both exist to persuade someone who has not committed, and
 * this reader already has. Dropping them onto a screen that re-pitches the thing they just started is
 * the fastest way to make the in-page question feel decorative.
 */
export const OPENING_ANSWER_STEP: Step = 'm1-activity';

/**
 * How a reader reached the preview. Stamped on every event, so the snippet's whole reason for existing
 * is measurable: `question` arrivals against `link` arrivals, on the same funnel, per guide page.
 *
 * Without this the snippet could lift completions and we would not be able to prove it, because both
 * entry paths land on the same route with the same `?src=`.
 */
export const PREVIEW_ENTRY = {
  /** A plain CTA link on the guide page. The original placement, and the control. */
  LINK: 'link',
  /** The in-page first question. The reader answered before arriving. */
  QUESTION: 'question',
} as const;

export type PreviewEntry = (typeof PREVIEW_ENTRY)[keyof typeof PREVIEW_ENTRY];

/**
 * The accepted `?a1=` values, which are the store's own swipe vocabulary rather than a friendlier
 * synonym set.
 *
 * Strict, and rejecting rather than guessing, for the same reason `?src=` is (see `guide-source.ts`):
 * the value decides what gets written into a reader's answers. A generous parser that read `?a1=yes`
 * as a right-swipe would also read a typo or a crawler's mangled query as one, silently fabricating an
 * answer the reader never gave and then scoring an archetype off it. We author every link that carries
 * this parameter, so there is no one to be generous towards.
 */
const DIRECTIONS: Readonly<Record<string, SwipeDirection>> = {
  left: 'left',
  right: 'right',
};

/** Reduce an untrusted `?a1=` to a swipe direction, or null when it is absent or not one of ours. */
export function normaliseOpeningAnswer(raw: string | string[] | undefined): SwipeDirection | null {
  // An array means the param repeated (`?a1=left&a1=right`). First wins, matching `?src=`; there is no
  // meaningful way to hold two answers to one card.
  const value = Array.isArray(raw) ? raw[0] : raw;

  if (!value) {
    return null;
  }

  return DIRECTIONS[value.trim().toLowerCase()] ?? null;
}

/**
 * Read and normalise `?a1=` from the browser's own URL.
 *
 * Client-side for exactly the reason `?src=` is: awaiting `searchParams` in the page opts the whole
 * route into dynamic rendering, which disables the `dynamicParams` 404 and re-renders every visit for
 * an audience arriving cold from search. Returns null during SSR, where there is no URL, which is the
 * same as "no opening answer" and renders the welcome screen.
 */
export function readOpeningAnswerFromLocation(): SwipeDirection | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return normaliseOpeningAnswer(new URLSearchParams(window.location.search).get(OPENING_ANSWER_PARAM) ?? undefined);
}

/**
 * Bank the opening answer against the deck's FIRST card and jump to the deck.
 *
 * 🔴 THE CARD MUST BE THE ONE THE SNIPPET SHOWED. It is read from the pack rather than named here, so
 * the snippet and the seed cannot drift: `preview-copy.ts` trims the deck from eight cards to six, and
 * a hardcoded card id would survive a re-trim that moved the first card, seeding an answer to a
 * question no reader was asked. `opening-answer.spec.ts` asserts the two agree for every vertical.
 *
 * `setM1Cursor(1)` is belt to `setM1Answer`'s braces. `M1Activity` derives its own start position by
 * walking forward while cards are answered, so the seeded answer alone already puts the reader on card
 * two; the cursor is what carries that across a refresh, and writing both means neither path can be the
 * one that regresses.
 *
 * Returns whether anything was seeded, so the caller can tell an applied answer from a no-op on a pack
 * with an empty deck.
 */
export function applyOpeningAnswer(store: LabsStoreApi, pack: VerticalPack, direction: SwipeDirection): boolean {
  const firstCard = pack.m1.cards[0];

  if (!firstCard) {
    return false;
  }

  const { goTo, setM1Answer, setM1Cursor } = store.getState();

  setM1Answer(firstCard.id, direction);
  setM1Cursor(1);
  goTo(OPENING_ANSWER_STEP);

  return true;
}
